import { wrapReducer } from "@client/content/lib/helper";
import { type FailureActionData } from "@client/lib/actions";
import { Status } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import {
  Actions,
  type ApplySuccessActionData,
  type InitBeginActionData,
  type InitSuccessActionData,
  type LoadPageSuccessActionData,
  type SetShowingModalActionData,
  type ShowProfileActionData,
  type SubmitVoteSuccessActionData,
  type UpdateProfilePictureSuccessActionData
} from "./actions";
import { type Leaderboard } from "./types";

const reducer = (state: Leaderboard.AppState = defaultState, action: ReduxAction<any>): Leaderboard.AppState => {
  switch (action.type) {
    case Actions.InitBegin: {
      const { sourceId } = action.params as InitBeginActionData;
      return {
        ...state,
        sourceId,
        status: Status.Loading
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: Status.Failed
      };
    }
    case Actions.InitSuccess: {
      const { current, countryCode, referring, rewards, source, sourceId } = action.data as InitSuccessActionData;
      return {
        ...state,
        current,
        countryCode,
        referring,
        rewards: rewards.sort((a, b) => b.sortOrder - a.sortOrder),
        sourceId,
        source,
        status: Status.Ready,
        vote: referring
          ? {
              error: null,
              item: referring,
              status: "Ready",
              step: 1
            }
          : null
      };
    }

    case Actions.LoadPageBegin: {
      return {
        ...state,
        status: "LoadingPage"
      };
    }
    case Actions.LoadPageFailure: {
      return {
        ...state,
        status: Status.Ready
      };
    }
    case Actions.LoadPageSuccess: {
      const { hasMore, page, pageSize, items } = action.data as LoadPageSuccessActionData;
      return {
        ...state,
        hasMore,
        items: [...state.items.slice(0, (page - 1) * pageSize), ...items],
        page,
        status: Status.Ready
      };
    }

    case Actions.SetShowingModal: {
      const { modal } = action.data as SetShowingModalActionData;
      return {
        ...state,
        showingModal: modal,
        vote:
          modal?.type === "Vote"
            ? {
                error: null,
                item: state.items.find((x) => x.userId === modal.ref)!,
                status: "Ready",
                step: 1
              }
            : null
      };
    }

    case Actions.ShowProfile: {
      const { hasVotedNow } = action.data as ShowProfileActionData;
      return {
        ...state,
        allowUploadProfilePicture: hasVotedNow,
        showingModal: { ref: null, type: "Profile" }
      };
    }

    case Actions.SubmitVoteBegin: {
      return {
        ...state,
        vote: {
          ...state.vote!,
          error: null,
          status: "Submitting"
        }
      };
    }
    case Actions.SubmitVoteFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        vote: {
          ...state.vote!,
          error,
          status: "Ready"
        }
      };
    }
    case Actions.SubmitVoteSuccess: {
      const { current, item } = action.data as SubmitVoteSuccessActionData;
      return {
        ...state,
        current,
        showingModal: current ? { ref: "", type: "Profile" } : state.showingModal,
        vote: {
          ...state.vote!,
          item,
          step: state.current ? 1 : 2,
          status: "Ready"
        }
      };
    }

    case Actions.ApplyBegin: {
      return {
        ...state,
        status: "Applying"
      };
    }
    case Actions.ApplyFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: Status.Ready
      };
    }
    case Actions.ApplySuccess: {
      const { item } = action.data as ApplySuccessActionData;
      return {
        ...state,
        allowUploadProfilePicture: true,
        current: item,
        showingModal: { ref: "", type: "Profile" },
        status: Status.Ready
      };
    }

    case Actions.UpdateProfilePicture: {
      const { media } = action.data as UpdateProfilePictureSuccessActionData;
      return {
        ...state,
        current: {
          ...state.current!,
          profilePictureMediaId: media?.mediaId || null,
          profilePictureSourceId: media?.sourceId || null,
          profilePictureMediaSourceKey: media?.mediaSourceKey || null
        }
      };
    }

    default:
      return state;
  }
};

export const defaultState: Leaderboard.AppState = {
  current: null,
  error: null,
  hasMore: true,
  allowUploadProfilePicture: false,
  id: "",
  countryCode: null,
  items: [],
  page: 1,
  referring: null,
  rewards: [],
  showingModal: null,
  source: null,
  sourceId: null,
  status: Status.None,
  vote: null
};
export const previewState: Leaderboard.AppState = {
  ...defaultState,
  hasMore: false,
  items: [],
  page: 1,
  status: Status.Ready
};
export default wrapReducer({ defaultState, reducer, previewState });
